/**
 *
 * Provides our client side phone validation
 */

import StringValidation from './String';

/**
 * Uses same regex as backend!
 * Make sure it consistent if going to change it
 */
const PHONE_REGEX = /(\d.*){9}/;

const DUTCH_PHONE_ALLOWED_CHARS_REGEX = /^[0-9\s\-\+\(\)]{6,}$/;
const DUTCH_PHONE_COGNITO_REGEX = /^\+31\d{9}$/;

export const isValid = (phone: string | null | undefined) => {
  if (phone === null || !StringValidation.isString(phone)) return false;

  return PHONE_REGEX.test(phone);
};

/**
 * Live input: only digits and `+`, with `+` allowed only as the first character.
 * Aligns with the `+` rules in {@link normalizeDutchPhoneNumber}.
 */
export const sanitizePhoneInput = (value: string): string =>
  value.replace(/(?!^)\+/g, '').replace(/[^\d+]/g, '');

/**
 * Normalizes Dutch phone input to Cognito's E.164 format.
 *
 * Accepts local and international Dutch formats such as:
 * 06 12345678, 020 222 3571, +31 6 12345678, +31 (0)20 222 3571
 *
 * Returns a `+31...` number that Cognito accepts, or null when the
 * input cannot be normalized to a Dutch phone number.
 */
const normalizeDutchPhoneNumber = (phone: string | null | undefined) => {
  if (phone === null || !StringValidation.isString(phone)) return null;

  const trimmedPhone = phone.trim();
  if (!DUTCH_PHONE_ALLOWED_CHARS_REGEX.test(trimmedPhone)) return null;

  const plusMatches = trimmedPhone.match(/\+/g) ?? [];
  if (plusMatches.length > 1) return null;
  if (plusMatches.length === 1 && !trimmedPhone.startsWith('+')) return null;

  let normalizedPhone = trimmedPhone.replace(/[\s\-\(\)]/g, '');

  if (normalizedPhone.startsWith('+310')) {
    normalizedPhone = `+31${normalizedPhone.slice(4)}`;
  }

  if (normalizedPhone.startsWith('0031')) {
    normalizedPhone = `+31${normalizedPhone.slice(4)}`;
  } else if (normalizedPhone.startsWith('0')) {
    normalizedPhone = `+31${normalizedPhone.slice(1)}`;
  }

  return DUTCH_PHONE_COGNITO_REGEX.test(normalizedPhone)
    ? normalizedPhone
    : null;
};

/**
 * Checks if the phone number can be normalized to a Cognito-compatible Dutch number.
 */
const isValidDutchPhoneNumber = (phone: string | null | undefined) =>
  normalizeDutchPhoneNumber(phone) !== null;

const maskPhone = (phone: string): string => {
  if (phone.length <= 5) return phone;
  return phone.slice(0, 3) + '•'.repeat(phone.length - 5) + phone.slice(-2);
};

export default {
  isValid,
  isValidDutchPhoneNumber,
  normalizeDutchPhoneNumber,
  maskPhone,
  sanitizePhoneInput,
};
